import { useMutation } from '@apollo/react-hooks';
import { Box, Grid, MenuItem, Select, TextField, Typography } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import * as _ from 'lodash';
import { useSnackbar } from 'notistack';
import React, { useEffect } from 'react';
import { DialogForm } from '../../components/DialogForm';
import { EDIT_SERVICE } from '../graphQL/queries';
import { formatString } from '../../utils/helpers';
const useStyles = makeStyles(theme => ({
  dialog: {
    width: '100%'
  },
  dialogTitle: {
    padding: theme.spacing(2),
    borderBottom: '1px solid'
  },
  progress: {
    color: theme.palette.background.paper
  },
  textField: {
    width: '90%'
  },
  grid: {
    width: '100%',
    borderBottom: '1px solid #EBEBEB'
  },
  headers: {
    color: theme.palette.text.secondary
  }
}));

const retryFrequencies = [
  { value: 0, label: '0' },
  { value: 1, label: '1' },
  { value: 2, label: '2' },
  { value: 3, label: '3' },
  { value: 5, label: '5' },
  { value: 10, label: '10' },
  { value: 15, label: '15' },
  { value: 30, label: '30' },
  { value: 60, label: '60' }
];

const maxRetries = [
  { value: 0, label: '0' },
  { value: 5, label: '5' },
  { value: 10, label: '10' },
  { value: 25, label: '25' },
  { value: 50, label: '50' }
];

interface ParticipantServiceProps {
  dialogOpen: true | false;
  handleDialogClose: any;
  handleDialogSubmit: any;
  kpiAttributes: any;
  workflowCode: any;
  supportUser: true | false;
}

const EditServiceDialog = (props: ParticipantServiceProps) => {
  const classes = useStyles();
  const { enqueueSnackbar } = useSnackbar();
  const [editService] = useMutation(EDIT_SERVICE);
  const [formInfo, setFormInfo] = React.useState<any>({});
  const [serviceKPIAtrributesValues, setServiceKPIAtrributesValues] = React.useState<any>({});
  const [operationCodeMenu, setOperationCode] = React.useState<any>([]);
  useEffect(() => {
    const kpiMap: any = [];
    const kpiAttributeValue: any = {};

    const availList = _.filter(props.kpiAttributes || [], (o: any) => o.workflowCode === props?.workflowCode);
    _.keys(availList?.[0]?.kpiAttributes || []).forEach((attribute: any) => {
      const kpiValues: any = [];
      (availList?.[0]?.kpiAttributes[attribute] || []).forEach((value: any) => {
        kpiValues.push(value);
      });
      kpiMap.push(attribute);
      kpiAttributeValue[attribute] = kpiValues;
    });
    setServiceKPIAtrributesValues(kpiAttributeValue);
    let operations: any = {};
    let operationMap: any = {};
    const serviceNameFormObj = _.filter(props.kpiAttributes || [], (o: any) => o.workflowCode === props?.workflowCode);
    (serviceNameFormObj?.[0]?.operations || []).forEach((operation: any) => {
      operationMap[operation.operationCode] = operation.operationName;
      operations[operation.operationCode] = {
        retryFrequency: operation?.retryFrequency || null,
        maxRetries: operation?.maxRetries || null,
        noOpsResp: JSON.stringify(operation?.noOpsResp),
        asynOperationCode: operation?.asynOperationCode,
        operationName: operation?.operationName,
        initiator: operation?.initiator,
        operationCode: operation?.operationCode,
        asynAttributes: operation?.asynAttributes ? formatString(operation?.asynAttributes) : '',
        autoRetry: (operation?.maxRetries > 0 && operation?.maxRetries !== null) || false
      };
    });

    const operationNameList: any = [];
    const operationCodeList: any = [];
    _.keys(operationMap || []).forEach(key => {
      const name = operationMap[key];
      operationCodeList.push(key);
      operationNameList.push(name);
    });
    setOperationCode(operationCodeList);
    const operationsList: any = {};
    _.keys(operations || []).forEach(key => {
      const obj = { ...operations[key] };
      obj.operationName = operationMap[key];
      operationsList[key] = obj;
    });
    setFormInfo({
      operations: operationsList,
      workflowCode: props?.workflowCode,
      operationsAttributes: operations
    });
  }, [props.kpiAttributes, props?.workflowCode]);

  const handleOperationDataChange = (e: any, operation: any, field: string) => {
    const obj = formInfo.operationsAttributes[operation];
    obj[field] =
      field !== 'noOps' && field !== 'autoRetry' && field !== 'useAggregator' ? e.target.value : e.target.checked;

    setFormInfo({
      ...formInfo,
      operationsAttributes: {
        ...formInfo.operationsAttributes
      }
    });
  };

  const handleDialogSubmit = () => {
    const operationsList: {
      operationName: string;
      operationCode: string;
      workflowCode: string;
      initiator: string;
      noOpsResp: any;
      asynOperationCode: string;
      asynAttributes: any;
      retryFrequency: any;
      maxRetries: any;
    }[] = [];
    _.keys(formInfo.operationsAttributes || []).forEach(key => {
      const obj = {
        operationName: formInfo.operationsAttributes[key]?.operationName,
        operationCode: key,
        workflowCode: props?.workflowCode,
        initiator: formInfo.operationsAttributes[key]?.initiator,
        noOpsResp: formInfo.operationsAttributes[key]?.noOpsResp
          ? formatString(formInfo.operationsAttributes[key]?.noOpsResp)
          : { responseStatus: 'Acknowledged' },
        asynOperationCode: formInfo.operationsAttributes[key]?.asynOperationCode || '',
        asynAttributes: formInfo.operationsAttributes[key]?.asynAttributes
          ? JSON.stringify(formInfo.operationsAttributes[key]?.asynAttributes)
          : null,
        retryFrequency: formInfo.operationsAttributes[key]?.retryFrequency || null,
        maxRetries: formInfo.operationsAttributes[key]?.maxRetries || null
      };
      operationsList.push(obj);
    });

    editService({
      variables: {
        editOperations: operationsList
      }
    })
      .then(response1 => {
        enqueueSnackbar('Service has been edited successfully !!', {
          variant: 'success',
          preventDuplicate: true
        });
        props.handleDialogSubmit();
        return response1;
      })
      .catch(error => {
        enqueueSnackbar('Unable to edit', { variant: 'error', preventDuplicate: true });
        props.handleDialogSubmit();
      });
  };

  const getLatestValues = (data: any) => {
    const info = { ...formInfo };
    setFormInfo({
      ...info,
      ...data
    });
  };

  return (
    <div>
      <DialogForm
        openFn={props.dialogOpen}
        closeFn={props.handleDialogClose}
        dialogId="Edit-service-dialog"
        title={'Edit Service'}
        dataConfig={[]}
        handleDialogClose={props.handleDialogClose}
        handleDialogSubmit={handleDialogSubmit}
        submitlabel="Edit Service"
        getLatestValues={getLatestValues}
        data={props.workflowCode}
        supportUser={props.supportUser}
        tooltip={'sample text'}
      >
        <>
          <Box m={1}>
            <Grid item xs={3} justify="center" alignItems="flex-end">
              <Typography variant="subtitle1" noWrap>
                KPI Attributes
              </Typography>
            </Grid>
            <Box className={classes.grid} style={{ padding: '8px' }}>
              <Grid container spacing={3}>
                <Grid item xs={3}>
                  <Typography className={classes.headers}>Attribute</Typography>
                </Grid>
                <Grid item xs={1}>
                  <Typography className={classes.headers}>Value</Typography>
                </Grid>
              </Grid>
            </Box>
            <Box m={2}>
              {props?.workflowCode &&
                Object.keys(serviceKPIAtrributesValues || {}).map((attribute: any) => (
                  <Grid container spacing={3} alignItems="flex-end">
                    <Grid item xs={3} justify="center" alignItems="flex-end">
                      <Typography noWrap>{attribute}</Typography>
                    </Grid>
                    <Grid item xs={8} justify="center" alignItems="flex-end">
                      <TextField
                        data-id="kpiValue"
                        id="kpiValue"
                        disabled={true}
                        className={classes.textField}
                        value={serviceKPIAtrributesValues[attribute] || ''}
                        onChange={e => 'ok'}
                      ></TextField>
                    </Grid>
                  </Grid>
                ))}
            </Box>
          </Box>
          <Box m={1}>
            <Box className={classes.grid}>
              <Typography variant="subtitle1" style={{ padding: '8px' }}>
                Operations
              </Typography>
            </Box>
            <Box className={classes.grid} style={{ padding: '8px' }}>
              <Grid container spacing={3}>
                <Grid item xs={3}>
                  <Typography className={classes.headers}>Name</Typography>
                </Grid>
                <Grid item xs={1}>
                  <Typography className={classes.headers}>Retry Frequency</Typography>
                </Grid>
                <Grid item xs={1}>
                  <Typography className={classes.headers}>Maximum retries</Typography>
                </Grid>
                <Grid item xs={2}>
                  <Typography className={classes.headers}>Async Operation</Typography>
                </Grid>
                <Grid item xs={2}>
                  <Typography className={classes.headers}>No Ops Response</Typography>
                </Grid>
                <Grid item xs={2}>
                  <Typography className={classes.headers}>Retry Attribute</Typography>
                </Grid>
              </Grid>
            </Box>
            <Box m={2}>
              {props?.workflowCode &&
                Object.keys(formInfo?.operations || {}).map((operationCode: any) => (
                  <Grid container spacing={3} alignItems="flex-end">
                    <Grid item xs={3} justify="center" alignItems="flex-end">
                      <Typography noWrap>{formInfo.operations[operationCode].operationName}</Typography>
                    </Grid>
                    <Grid item xs={1} justify="center" alignItems="flex-end">
                      <Select
                        id={`retry-frequency-${operationCode}`}
                        data-id={`retry-frequency-${operationCode}`}
                        className={classes.textField}
                        label={'Retry Frequency'}
                        value={formInfo.operationsAttributes[operationCode]?.retryFrequency}
                        disabled={props.supportUser ? props.supportUser : false}
                        onChange={e => handleOperationDataChange(e, operationCode, 'retryFrequency')}
                      >
                        {retryFrequencies?.map((item, key) => (
                          <MenuItem key={key} value={item.value}>
                            {item.label}
                          </MenuItem>
                        ))}
                      </Select>
                    </Grid>
                    <Grid item xs={1} justify="center" alignItems="flex-end">
                      <Select
                        id={`maximum-retry-${operationCode}`}
                        data-id={`maximum-retry-${operationCode}`}
                        className={classes.textField}
                        label={'Maximum Retries'}
                        value={formInfo.operationsAttributes[operationCode].maxRetries}
                        disabled={props.supportUser ? props.supportUser : false}
                        onChange={e => handleOperationDataChange(e, operationCode, 'maxRetries')}
                      >
                        {maxRetries?.map((item, key) => (
                          <MenuItem key={key} value={item.value}>
                            {item.label}
                          </MenuItem>
                        ))}
                      </Select>
                    </Grid>
                    <Grid item xs={2} justify="center" alignItems="flex-end">
                      <Select
                        id={`async-operation-${operationCode}`}
                        data-id={`async-operation-${operationCode}`}
                        className={classes.textField}
                        label={'Async Operation'}
                        value={formInfo.operationsAttributes[operationCode].asynOperationCode}
                        disabled={props.supportUser ? props.supportUser : false}
                        onChange={e => handleOperationDataChange(e, operationCode, 'asynOperationCode')}
                      >
                        {operationCodeMenu?.map((item: any) => [
                          <MenuItem disabled={!item} key={item} value={item}>
                            {item}
                          </MenuItem>
                        ])}
                      </Select>
                    </Grid>
                    <Grid item xs={2}>
                      <TextField
                        data-id={`noOpsResp-${operationCode}`}
                        id={`noOpsResp-${operationCode}`}
                        disabled={props.supportUser ? props.supportUser : false}
                        className={classes.textField}
                        value={formInfo.operationsAttributes[operationCode].noOpsResp}
                        onChange={e => handleOperationDataChange(e, operationCode, 'noOpsResp')}
                      ></TextField>
                    </Grid>
                    <Grid item xs={2}>
                      <TextField
                        data-id={`asynAttributes-${operationCode}`}
                        id={`asynAttributes-${operationCode}`}
                        disabled={props.supportUser ? props.supportUser : false}
                        className={classes.textField}
                        value={formInfo.operationsAttributes[operationCode].asynAttributes}
                        onChange={e => handleOperationDataChange(e, operationCode, 'asynAttributes')}
                      ></TextField>
                    </Grid>
                  </Grid>
                ))}
            </Box>
          </Box>
        </>
      </DialogForm>
    </div>
  );
};
export default EditServiceDialog;
